import { strings } from "i18n/Strings";

/**
 * Type definition for validation functions
 * @param value - The string value to validate
 * @param key - Optional field name/key for error messages
 * @returns Error message string if validation fails, undefined if validation passes
 */
type ValidationFunction = (value: string, key?: string) => string | undefined;

/**
 * Validates that a field is required (not empty, null, or undefined)
 * @param value - The value to validate
 * @param key - Optional field name for error message
 * @returns Error message if validation fails, undefined if validation passes
 */
export const requiredValidator: ValidationFunction = (value: string, key?: string) => {
    if (value === undefined || value === null) {
        return `${key ?? 'Field'} is required`;
    }
    if (typeof value === 'string' && value.trim() === '') {
        return `The ${key ?? 'Field'} is required`;
    }
    return undefined;
}

/**
 * Creates a validator function that checks if a value exceeds maximum length
 * @param max - Maximum allowed length
 * @returns Validation function that returns error message if length exceeds max
 */
export const maxLengthValidator = (max: number): ValidationFunction => {
    return (value) => {
        if (value && value.length > max) {
            return `Must be ${max} characters or less`;
        }
    };
};

/**
 * Creates a validator function that checks if a value meets minimum length requirement
 * @param min - Minimum required length
 * @returns Validation function that returns error message if length is less than min
 */
export const minLengthValidator = (min: number): ValidationFunction => {
    return (value) => {
        if (value && value.length < min) {
            return `Must be at least ${min} characters`;
        }
    };
};

/**
 * Validates email format using regex pattern
 * @param value - The email string to validate
 * @param key - Optional field name for error message
 * @returns Error message if validation fails, undefined if validation passes
 */
export const emailValidator: ValidationFunction = (value, key) => {
    // Regex pattern for email validation
    const emailRegex =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z-0-9]+\.)+[a-zA-Z]{2,}))$/;

    // Returns "This field is required" because the value is empty
    if (requiredValidator(value)) {
        return requiredValidator(value, key);
    }

    if (value && !emailRegex.test(value)) {
        return strings.displayText.invalidEmailText;
    }
};

/**
 * Validates phone number format using regex pattern
 * @param value - The phone number string to validate
 * @returns Error message if validation fails, undefined if validation passes
 */
export const phone: ValidationFunction = (value) => {
    if (
        value &&
        !/^(?:\+\d{1,3})?[1-9]\d{9,14}$/i.test(value)
    ) {
        return "Invalid phone number";
    }
};

/**
 * Validates that a value contains only numeric characters
 * @param value - The string value to validate
 * @returns Error message if validation fails, undefined if validation passes
 */
export const onlyNumbers: ValidationFunction = (value) => {

    if (value && !/^[0-9]*$/i.test(value)) {
        return "Only numbers are allowed";
    }
};

/**
 * Validates that a value contains only alphabetic characters and spaces
 * @param value - The string value to validate
 * @returns Error message if validation fails, undefined if validation passes
 */
export const onlyCharacters: ValidationFunction = (value) => {
    if (value && !/^[a-zA-Z\s]*$/i.test(value)) {
        return "Only characters are allowed";
    }
};

/**
 * Validates IBAN (International Bank Account Number) format
 * @param value - The IBAN string to validate
 * @returns Error message if validation fails, undefined if validation passes
 */
export const IBANBankNumberValidation: ValidationFunction = (value: string) => {
    const ibanRegex = /^[A-Z]{2}\d{2}[A-Z]{4}\d{6}\d{8}$/;
    if (value && !ibanRegex.test(value)) {
        return "Invalid Bank Account Format."
    }
}